import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import Loader from "@/components/Loader";
import { useAuth } from "@/lib/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export default function Tickets() {
  const [types, setTypes] = useState(null);
  const [groups, setGroups] = useState(null);
  const [cart, setCart] = useState({});
  const { user } = useAuth();
  const nav = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/tickets/types").then(({ data }) => setTypes(data));
    api.get("/config/tickets").then(({ data }) => setGroups(data));
  }, []);

  const total = (types || []).reduce((s, t) => s + (cart[t.id] || 0) * (t.price_chf || 0), 0);

  const setQty = (id, delta) => {
    setCart((c) => ({ ...c, [id]: Math.max(0, (c[id] || 0) + delta) }));
  };

  const checkout = async () => {
    if (!user) { toast.info("Connectez-vous pour finaliser l'achat"); return nav("/login?next=/tickets"); }
    const items = (types || []).filter(t => cart[t.id]).map(t => ({ id: t.id, name: t.name, price_chf: t.price_chf, quantity: cart[t.id] }));
    if (!items.length) return toast.error("Sélectionnez au moins un billet");
    setSubmitting(true);
    try {
      const { data } = await api.post("/checkout", { items, kind: "ticket" });
      toast.success(`Commande ${data.order_id.slice(0, 8)} créée (CHF ${data.total_chf.toFixed(2)})`);
      nav(`/order/${data.order_id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec du checkout"); }
    finally { setSubmitting(false); }
  };

  if (!types) return <Loader />;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="text-4xl font-black gradient-text inline-block">Billetterie</h1>
      <p className="text-neutral-400 mt-2">Sélectionnez vos billets — paiement sécurisé via Stripe.</p>

      <div className="grid lg:grid-cols-3 gap-4 mt-8">
        <div className="lg:col-span-2 space-y-3">
          {types.map((t) => (
            <div key={t.id} className="card-hsg p-5 flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl flex items-center justify-center text-2xl font-black" style={{ background: t.color + "22", color: t.color, border: `1px solid ${t.color}55` }}>🎫</div>
              <div className="flex-1 min-w-0">
                <div className="font-bold">{t.name}</div>
                <div className="text-neutral-400 text-sm">{t.description}</div>
                <div className="text-xs text-neutral-500 mt-1">Public : {t.audience}</div>
              </div>
              <div className="text-right">
                <div className="font-black text-lg text-white">CHF {Number(t.price_chf).toFixed(0)}.-</div>
                <div className="inline-flex items-center gap-2 mt-2 bg-neutral-900 rounded-lg">
                  <button onClick={() => setQty(t.id, -1)} className="px-3 py-1 hover:bg-neutral-800 rounded-l-lg" data-testid={`qty-minus-${t.id}`}>−</button>
                  <span className="px-2 min-w-8 text-center" data-testid={`qty-value-${t.id}`}>{cart[t.id] || 0}</span>
                  <button onClick={() => setQty(t.id, 1)} className="px-3 py-1 hover:bg-neutral-800 rounded-r-lg" data-testid={`qty-plus-${t.id}`}>+</button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="card-hsg p-5 sticky top-24 h-fit">
          <div className="font-bold text-lg mb-3">Résumé</div>
          <div className="space-y-2 max-h-64 overflow-auto">
            {(types || []).filter(t => cart[t.id]).map(t => (
              <div key={t.id} className="flex justify-between text-sm">
                <span>{t.name} × {cart[t.id]}</span>
                <span>CHF {(cart[t.id] * t.price_chf).toFixed(2)}</span>
              </div>
            ))}
            {total === 0 && <p className="text-neutral-500 text-sm">Panier vide</p>}
          </div>
          <div className="border-t border-neutral-800 mt-3 pt-3 flex justify-between font-bold">
            <span>Total</span><span>CHF {total.toFixed(2)}</span>
          </div>
          <button disabled={submitting || total === 0} onClick={checkout} className="brand-gradient-btn w-full py-3 rounded-xl mt-4" data-testid="checkout-btn">
            {submitting ? "Création…" : "Passer commande"}
          </button>
          {!user && <p className="text-xs text-neutral-500 mt-2">Connectez-vous pour finaliser.</p>}
        </div>
      </div>

      {groups && Array.isArray(groups) && groups.length > 0 && (
        <section className="mt-14">
          <h2 className="text-2xl font-bold mb-4">📚 Détails des tarifs</h2>
          <div className="space-y-4">
            {groups.map((g) => (
              <div key={g.id} className="card-hsg p-5">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-bold text-lg">{g.title}</h3>
                  <span className="text-neutral-400 text-sm">{g.rangeLabel}</span>
                </div>
                <div className="space-y-2">
                  {(g.subs || []).map((s, i) => (
                    <div key={i} className="bg-neutral-900 rounded-lg p-3">
                      <div className="font-semibold">{s.name}</div>
                      {s.description && <p className="text-neutral-400 text-sm mt-1 whitespace-pre-line">{s.description}</p>}
                      {(s.bullets || []).length > 0 && (
                        <ul className="mt-2 text-sm">
                          {s.bullets.map((b, j) => (
                            <li key={j} className="flex justify-between">
                              <span className={b.bold ? "font-bold" : ""}>{b.label}</span>
                              <span className={b.bold ? "font-bold" : ""}>{b.value}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
